import {
  FilterType,
  CustomFilterType,
  NativeFilterTypes,
} from 'design-system-web';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { getTimeZoneByGeoLocation } from 'async-timing';
import {
  ExternalPayerType,
  InvoiceeInfo,
  CHARGE_STATUS,
} from 'lane-shared/domains/billingPayments/types';
import { ChargeDisplayStatusEnum } from 'domains-billing-types';
import { searchWorkplaceMembersAndCompanies } from './searchWorkplaceMembersAndCompanies';
import { getCustomerNameById } from './getCustomerNameById';
import { useChannelAdminContext } from 'hooks';
import { useChargesFilteringV2Enabled } from 'react-hooks';

const PER_PAGE = 50;

export const DEFAULT_FILTER_PARAMS = {
  page: 0,
  pageSize: PER_PAGE,
  total: 0,
};

export type ChargeQueryString = {
  page?: string;
  pageSize?: string;
  total?: string;
  status?: string;
  createdAt?: string;
  payer?: string;
};

const getChargeStatusOptions = (isChargesFilteringV2Enabled: boolean) => [
  { label: CHARGE_STATUS.CHARGE_PENDING, value: ChargeDisplayStatusEnum.PENDING },
  { label: CHARGE_STATUS.CHARGE_UNPAID, value: ChargeDisplayStatusEnum.UNPAID },
  { label: CHARGE_STATUS.CHARGE_OVERDUE, value: ChargeDisplayStatusEnum.OVERDUE },
  { label: CHARGE_STATUS.CHARGE_PAID, value: ChargeDisplayStatusEnum.PAID },
  { label: CHARGE_STATUS.CHARGE_VOIDED, value: ChargeDisplayStatusEnum.VOIDED },
  ...(isChargesFilteringV2Enabled
    ? [
        {
          label: CHARGE_STATUS.CHARGE_EXTERNALLY_MANAGED,
          value: ChargeDisplayStatusEnum.EXTERNALLY_MANAGED,
        },
        {
          label: CHARGE_STATUS.CHARGE_NO_INVOICE,
          value: ChargeDisplayStatusEnum.NO_INVOICE,
        },
      ]
    : []),
];

export const useGetChargeTableFilters = (
  channelId: string | null
): (FilterType | CustomFilterType<any>)[] => {
  const { t } = useTranslation();
  const { channel } = useChannelAdminContext();
  const isChargesFilteringV2Enabled = useChargesFilteringV2Enabled();
  const timeZone = channel?.address?.geo
    ? getTimeZoneByGeoLocation({
        longitude: channel.address.geo[0],
        latitude: channel.address.geo[1],
      })
    : '';

  const loadCustomerOptions = useCallback(async (inputValue: string) => {
    if (!channelId) {
      return [];
    }

    const customers = await searchWorkplaceMembersAndCompanies(channelId)(inputValue);

    return customers.map((customer: InvoiceeInfo) => {
      const prefix =
        customer.type === ExternalPayerType.EXTERNAL_PAYER_TYPE_ACTIVATE_COMPANY
          ? 'c-'
          : 'u-';

      return {
        label: customer.name,
        value: prefix + customer._id,
      };
    });
  }, [channelId]);

  const loadDefaultOptions = async (value: string) => {
    const prefix = value[0];
    const id = value.slice(2);
    const customerType =
      prefix === 'c'
        ? ExternalPayerType.EXTERNAL_PAYER_TYPE_ACTIVATE_COMPANY
        : ExternalPayerType.EXTERNAL_PAYER_TYPE_ACTIVATE_USER;

    const customer = await getCustomerNameById(id, customerType);

    if (customer) {
      return [{ label: customer.name, value }];
    }

    return [];
  };

  return [
    {
      key: 'status',
      label: t('abp.chargeList.status'),
      type: NativeFilterTypes.Multiselect,
      options: getChargeStatusOptions(Boolean(isChargesFilteringV2Enabled)),
      isPromoted: true,
    },
    {
      key: 'createdAt',
      label: t('abp.chargeBillingReports.createdDate'),
      type: NativeFilterTypes.DateRange,
      isPromoted: true,
      hideLabel: true,
      timeZone,
    },
    {
      key: 'payer',
      label: t('abp.chargeBillingReports.customer'),
      type: NativeFilterTypes.Select,
      options: [],
      doTranslation: false,
      isPromoted: true,
      loadOptions: loadCustomerOptions,
      loadDefaultOptions,
      placeholder: t('abp.chargeBillingReports.searchCustomers'),
      noOptionsMessage: t('abp.chargeBillingReports.filter.searchOptions'),
    },
  ];
};
